import { createFilterOptions } from '@material-ui/lab';
import { Button, Icon } from 'copilot-design-system';
import { useFormikContext } from 'formik';
import { ComponentPropsWithoutRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import Autocomplete from 'src/components/Autocomplete';
import { BaseTextField } from 'src/legacy/components/TextField';
import {
  CLIENT_SOURCE_LABELS,
  CLIENT_SOURCE_OPTIONS,
  COMPANY_AGE_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  INDUSTRY_LABELS,
  INDUSTRY_OPTIONS,
} from 'src/routes/onboarding/setup/components/about-your-company/constants';
import {
  OnboardingContentLayout,
  OnboardingQuestionLabel,
} from 'src/routes/onboarding/setup/components/OnboardingContentLayout';
import { ToggleChipGroup } from 'src/routes/onboarding/setup/components/ToggleChipGroup';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { TrackEvent } from 'src/utils/AnalyticsUtils';

const onboardingAutocompleteFilter = createFilterOptions<string>();

type AutoCompleteProps = ComponentPropsWithoutRef<
  typeof Autocomplete<string, undefined, undefined, true>
>;
type AutoCompleteOnChangeType = NonNullable<AutoCompleteProps['onChange']>;

const OnboardingAutocomplete = ({
  labelsMap,
  addOptionLabel,
  placeholder,
  error,
  helperText,
  onBlur,
  onChange,
  value,
  ...props
}: {
  labelsMap: Record<string, string>;
  addOptionLabel: string;
  onChange?: (value: string | null, label: string) => void;
} & Pick<AutoCompleteProps, 'id' | 'value' | 'options'> &
  Pick<
    ComponentPropsWithoutRef<typeof BaseTextField>,
    'placeholder' | 'error' | 'helperText' | 'onBlur'
  >) => {
  const getOptionLabel = (option: string) =>
    option in labelsMap ? labelsMap[option] : option !== '' ? option : '';

  const internalOnChange: AutoCompleteOnChangeType = async (_, newValue) => {
    if (onChange) {
      await onChange(newValue, getOptionLabel(newValue ?? ''));
    }
  };

  return (
    <Autocomplete
      {...props}
      value={value}
      onChange={internalOnChange}
      getOptionLabel={getOptionLabel}
      filterOptions={(options, params) => {
        const filtered = onboardingAutocompleteFilter(options, params);
        const { inputValue } = params;
        const isExisting = options.some((option) => inputValue === option);
        if (inputValue.trim() === '' && !value) {
          filtered.unshift('');
        }
        if (inputValue.trim() !== '' && !isExisting) {
          filtered.unshift(inputValue);
        }
        return filtered;
      }}
      renderOption={(option, state) => {
        const isAddOption = option === '';
        const isAddedItem = !state.selected && !(option in labelsMap);

        let label = labelsMap[option] ?? '';
        if (isAddOption) {
          label = addOptionLabel;
        } else if (isAddedItem) {
          label = `Add "${option}"`;
        }

        return (
          <div className="flex items-center gap-2 h-5">
            {(isAddOption || isAddedItem) && (
              <Icon icon="Plus" className="w-3.5 h-3.5 text-inherit shrink-0" />
            )}
            <span className="leading-none whitespace-pre">{label}</span>
          </div>
        );
      }}
      renderInput={(params) => (
        <BaseTextField
          {...params}
          sizeVariant="medium"
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            className: '',
            endAdornment: null,
          }}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          onBlur={async (e) => {
            if (onChange) {
              const inputVal = e.target.value;
              if (typeof value === 'string' && labelsMap[value] === inputVal) {
                // do not save on blur if the value exists in the original options list
                return;
              }
              // awaiting onChange so that it is guaranteed to finish running before onBlur
              // otherwise there's a race condition where the previous value is used for validation
              await internalOnChange(e, inputVal, 'blur');
            }
            if (onBlur) {
              onBlur(e);
            }
          }}
        />
      )}
      freeSolo
      selectOnFocus
      handleHomeEndKeys
    />
  );
};

const validationSchema = toFormikValidationSchema(
  z.object({
    companyAge: z.string().min(1),
    companySize: z.string().min(1),
    industry: z
      .string({ required_error: 'You must enter a valid industry' })
      .trim()
      .min(1)
      .max(50, 'Your industry cannot be longer than 50 characters'),
    clientSource: z
      .string({
        required_error: 'You must enter a valid client manager',
      })
      .trim()
      .min(1)
      .max(50, 'Your client manager cannot be longer than 50 characters'),
  }),
);

export const AboutYourCompany = ({
  trackedEvents,
  onAnalyticsEventTracked,
}: {
  trackedEvents: TrackedEventsType;
  onAnalyticsEventTracked: (eventName: OnboardingStep) => void;
}) => {
  const { t } = useTranslation();
  const { isValid, setFieldValue, values, touched, errors, handleBlur } =
    useFormikContext<OnboardingFormValues>();

  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: ({ values: submitValues }) => {
        if (!trackedEvents['about-your-company']) {
          TrackEvent('Onboarding - About Your Company', {
            companyAge: submitValues.companyAge,
            companySize: submitValues.companySize,
            industry: submitValues.industry,
            clientSource: submitValues.clientSource,
          });

          // update the tracked events
          onAnalyticsEventTracked('about-your-company');
        }
      },
      validationSchema,
    });
  }, [setFormPageInfo, TrackEvent, trackedEvents, onAnalyticsEventTracked]);

  return (
    <>
      <OnboardingContentLayout
        title={t(($) => $.page_onboarding_company.about_your_company_title)}
        description={t(
          ($) => $.page_onboarding_company.about_your_company_description,
        )}
      >
        <div>
          <OnboardingQuestionLabel>
            How long have you had your business?
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={COMPANY_AGE_OPTIONS}
            value={values.companyAge}
            onOptionClick={(value, label) => {
              setFieldValue('companyAge', value);
              setFieldValue('companyAgeLabel', label);
            }}
          />
        </div>

        <div>
          <OnboardingQuestionLabel>
            How many people work at your company?
          </OnboardingQuestionLabel>
          <ToggleChipGroup
            options={COMPANY_SIZE_OPTIONS}
            value={values.companySize}
            onOptionClick={(value, label) => {
              setFieldValue('companySize', value);
              setFieldValue('companySizeLabel', label);
            }}
          />
        </div>

        <div>
          <label htmlFor="industry">
            <OnboardingQuestionLabel>
              What industry are you in?
            </OnboardingQuestionLabel>
          </label>
          <OnboardingAutocomplete
            id="industry"
            placeholder="Accounting, Marketing, etc."
            addOptionLabel="Add industry"
            value={values.industry}
            options={[...INDUSTRY_OPTIONS]}
            labelsMap={INDUSTRY_LABELS}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onChange={async (value, label) => {
              // setFieldValue is async (contrary to the type definitions)
              // awaiting it here so that we can avoid race conditions when it's called
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await setFieldValue('industry', value);
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await setFieldValue('industryLabel', label);
            }}
            error={Boolean(touched.industry && errors.industry)}
            helperText={touched.industry && errors.industry}
            onBlur={handleBlur}
          />
        </div>

        <div>
          <label htmlFor="clientSource">
            <OnboardingQuestionLabel>
              Where do you currently manage your list of clients?
            </OnboardingQuestionLabel>
          </label>
          <OnboardingAutocomplete
            id="clientSource"
            placeholder="Salesforce, HubSpot, etc."
            addOptionLabel={t(
              ($) => $.page_onboarding_company.add_client_management_tool,
            )}
            value={values.clientSource}
            options={[...CLIENT_SOURCE_OPTIONS]}
            labelsMap={CLIENT_SOURCE_LABELS}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onChange={async (value, label) => {
              // setFieldValue is async (contrary to the type definitions)
              // awaiting it here so that we can avoid race conditions when it's called
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await setFieldValue('clientSource', value);
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await setFieldValue('clientSourceLabel', label);
            }}
            error={Boolean(touched.clientSource && errors.clientSource)}
            helperText={touched.clientSource && errors.clientSource}
            onBlur={handleBlur}
          />
        </div>
      </OnboardingContentLayout>
      <Button
        className="w-full sm:max-w-[360px] h-12 text-lg ml-auto mr-auto mt-10"
        label="Continue"
        type="submit"
        disabled={!isValid}
      />
    </>
  );
};
